/**
 * Browser translation tools (Google Translate, Microsoft Translator) replace text nodes
 * with their own elements. React keeps references to the original nodes and crashes with
 * "Failed to execute 'removeChild' on 'Node'" when it later reconciles them.
 * See https://github.com/facebook/react/issues/11538
 */
const warnDetachedNode = (method: string, node: Node) => {
  if (process.env.NODE_ENV === 'production') return;

  // eslint-disable-next-line no-console
  console.warn(
    `[patchDomForBrowserTranslation] ${method} skipped: node is no longer a child of its expected parent.`,
    node
  );
};

const patchDomForBrowserTranslation = () => {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      warnDetachedNode('removeChild', child);
      return child;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warnDetachedNode('insertBefore', referenceNode);
      return newNode;
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};

export default patchDomForBrowserTranslation;
